import DescriptionIcon from '@material-ui/icons/Description'
import { hasPermission, Permissions } from 'Permissions'
import React, { useState } from 'react'
import { useNotify, useRefresh } from 'react-admin'
import { Link } from 'react-router-dom'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Wrap } from 'shared/components/Base/Wrap'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { MoreDropdown } from 'shared/components/Dropdown/MoreDropdown'
import { RadioButtonGroup } from 'shared/components/RadioButtonGroup/RadioButtonGroup'
import { Text } from 'shared/components/Text/Text'
import { lightGray1 } from 'shared/theme/colors'

import { issueTransition } from 'api/jobs'
import { StatusBadge } from 'components/Common/StatusBadge.styles'
import { IssueTransitions } from 'components/FSM/Transitions/IssueFSMTransitions'
import { getFormattedDate, getReadableErrorMessage } from 'helpers/utils'
import { Issue } from 'models/jobs'
import {
  AccessorialRequestStatus,
  AccessorialRequestType,
  IssueStatus,
  IssueType,
} from '../../helpers/constants'
import {
  IssueGrid,
  LabelValueGrid,
  RadioButtonGroupWrap,
  StyledFlexBox,
  StyledIssueSection,
} from './IssueSectionV2.styles'
import { ReportIssueButton } from './ReportIssueButton'

interface IssueItemProps {
  issue: Issue
  isLast: boolean
}

interface IssueStatusDropdownProps {
  issue: Issue
}

const IssueItem = ({ issue, isLast }: IssueItemProps) => {
  const dateCreated = getFormattedDate(issue.date_created, issue.stop?.location_timezone)
  const submittedBy =
    `${issue.reported_by?.first_name} ${issue.reported_by?.last_name}`.trim() || issue.reported_by?.email

  return (
    <Link to={`/issues/${issue.id}/show`} style={{ textDecoration: 'none' }}>
      <StyledIssueSection isColumn gap="24px" isLast={isLast}>
        <Text variant="header6">{issue.human_type}</Text>
        <IssueGrid>
          <FlexBox isColumn gap="8px">
            <FlexBox isColumn={false} gap="4px">
              <StatusBadge
                $backgroundColor={issue.human_status_background_color}
                $color={issue.human_status_color}
              >
                {issue.human_status}
              </StatusBadge>
            </FlexBox>
            <Text variant="header3" fontWeight={600}>
              {issue.status_updated_at_time_difference} ago
            </Text>
            <Text variant="paragraph2" fontColor={lightGray1} fontWeight={400}>
              {issue.status_updated_at && `Last updated at ${getFormattedDate(issue.status_updated_at)}`}
            </Text>
          </FlexBox>
          <FlexBox isColumn>
            <LabelValueGrid>
              <Text variant="header3" fontColor={lightGray1}>
                Stop
              </Text>
              <Text variant="label2">{issue.stop_label || '-'}</Text>
              <Text variant="header3" fontColor={lightGray1}>
                Check in
              </Text>
              <Text variant="label2">
                {issue.checked_in_at
                  ? getFormattedDate(issue.checked_in_at, issue.stop?.location_timezone)
                  : '-'}
              </Text>
              <Text variant="header3" fontColor={lightGray1}>
                Check out
              </Text>
              <Text variant="label2">
                {issue.checked_out_at
                  ? getFormattedDate(issue.checked_out_at, issue.stop?.location_timezone)
                  : '-'}
              </Text>
              <Text variant="header3" fontColor={lightGray1}>
                Wait time
              </Text>
              <Text variant="label2">{issue.wait_time || '-'}</Text>
            </LabelValueGrid>
          </FlexBox>
          <FlexBox isColumn>
            <LabelValueGrid>
              <Text variant="header3" fontColor={lightGray1}>
                Description
              </Text>
              <Text variant="label2">{issue.description}</Text>
              <Text variant="header3" fontColor={lightGray1}>
                Submitted by
              </Text>
              <Text variant="label2">
                {submittedBy} - {dateCreated}
              </Text>
              <Text variant="header3" fontColor={lightGray1}>
                Document
              </Text>
              {issue.document ? (
                <FlexBox gap="5px">
                  <DescriptionIcon color="primary" />
                  <Text variant="label2">{issue.document?.human_type}</Text>
                </FlexBox>
              ) : (
                '-'
              )}
            </LabelValueGrid>
          </FlexBox>
        </IssueGrid>
      </StyledIssueSection>
    </Link>
  )
}

const AccessorialRequestOptions = [
  {
    value: AccessorialRequestType.DETENTION,
    label: 'Detention',
  },
  {
    value: AccessorialRequestType.LAYOVER,
    label: 'Layover',
  },
]

const AccessorialOutcomeOptions = [
  {
    value: AccessorialRequestStatus.APPROVED,
    label: 'Approved',
  },
  {
    value: AccessorialRequestStatus.DENIED,
    label: 'Denied',
  },
]

export const IssueStatusDropdown = ({ issue }: IssueStatusDropdownProps) => {
  const isJobIssue = issue.issue_class === 'JOB'
  const [showDialog, setShowDialog] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [accessorialRequestOrOutcome, setAccessorialRequestOrOutcome] = useState('')
  const [dialogHeader, setDialogHeader] = useState('')
  const [showRadioButtonGroup, setShowRadioButtonGroup] = useState(false)
  const [radioButtonTitle, setRadioButtonTitle] = useState('')
  const [lumperTonuText, setLumperTonuText] = useState('')
  const [issueTransitionAction, setIssueTransitionAction] = useState('')
  const notify = useNotify()
  const refresh = useRefresh()

  const handleSuccess = () => {
    notify('Issue status updated!', { type: 'success' })
    setShowDialog(false)
    refresh()
  }

  const handleError = (error: any) => {
    notify(`Oops! Something went wrong: ${getReadableErrorMessage(error)}`, { type: 'error' })
  }

  async function handleConfirm() {
    let payload: object = { transition: issueTransitionAction }
    if (isJobIssue) {
      if (
        issue.is_accessorial &&
        issueTransitionAction === 'escalate_to_broker' &&
        accessorialRequestOrOutcome
      ) {
        payload = { ...payload, accessorial_type: accessorialRequestOrOutcome }
      } else if (issue.is_accessorial && issueTransitionAction === 'resolve' && accessorialRequestOrOutcome) {
        payload = { ...payload, accessorial_outcome: accessorialRequestOrOutcome }
      }
    }

    setIsLoading(true)
    try {
      await issueTransition(issue.id, payload, issue.issue_class)
      handleSuccess()
    } catch (err) {
      handleError(err)
    }
    setIsLoading(false)
  }

  const escalateToBrokerOption = {
    key: IssueStatus.EscalatedToBroker,
    label: 'Escalate to broker',
    onClick: () => {
      setDialogHeader('Mark as escalated to broker?')
      setIssueTransitionAction('escalate_to_broker')
      if (isJobIssue) {
        if ([IssueType.Lumper, IssueType.Tonu].includes(issue.type)) {
          setLumperTonuText(`Accessorial request: ${issue.human_type}`)
          setShowRadioButtonGroup(false)
        } else if (issue.type === IssueType.LongWait) {
          setShowRadioButtonGroup(true)
          setRadioButtonTitle('Accessorial request:')
        }
      }
      setShowDialog(true)
    },
  }

  const closeOption = {
    key: IssueStatus.Resolved,
    label: 'Close',
    onClick: () => {
      setDialogHeader('Mark issue as closed?')
      setIssueTransitionAction('resolve')
      if (isJobIssue) {
        if (issue.is_accessorial && issue.status === IssueStatus.EscalatedToBroker) {
          setShowRadioButtonGroup(true)
          setRadioButtonTitle(`${issue.accessorial_request_type} request`)
        } else {
          setShowRadioButtonGroup(false)
        }
      }
      setShowDialog(true)
    },
  }

  const reviewingOption = {
    key: IssueStatus.ResolutionPending,
    label: 'Reviewing',
    onClick: () => {
      issueTransition(
        issue.id,
        { transition: IssueTransitions.pendingResolution.transitionAction },
        issue.issue_class
      )
        .then(handleSuccess)
        .catch(handleError)
    },
  }

  function getIssueStatusDropdownOptions(issue: Issue) {
    if (issue.status === IssueStatus.Reported) {
      return [reviewingOption, escalateToBrokerOption, closeOption]
    }
    if (issue.status === IssueStatus.ResolutionPending) {
      return [escalateToBrokerOption, closeOption]
    }
    if (issue.status === IssueStatus.EscalatedToBroker) {
      return [closeOption]
    }
    return []
  }

  const statusDropdownOptions = getIssueStatusDropdownOptions(issue)
  const radioGroupOptions =
    issue.status === IssueStatus.EscalatedToBroker ? AccessorialOutcomeOptions : AccessorialRequestOptions

  return (
    <>
      <StatusBadge $backgroundColor={issue.human_status_background_color} $color={issue.human_status_color}>
        {issue.human_status}
      </StatusBadge>
      <MoreDropdown ariaLabel="Issue status" options={statusDropdownOptions} width={200} maxHeight={125} />
      <SimpleDialog isOpen={showDialog} onClose={() => setShowDialog(false)}>
        <Wrap width="472px" maxWidth="100%" align="left">
          <Text variant="header2">{dialogHeader}</Text>
          <Text variant="paragraph1">Driver will be notified in the app.</Text>
          {lumperTonuText && (
            <>
              <Box mt={4} />
              <Text variant="header4">{lumperTonuText}</Text>
            </>
          )}
          {isJobIssue && showRadioButtonGroup && (
            <RadioButtonGroupWrap>
              <RadioButtonGroup
                label={radioButtonTitle}
                options={radioGroupOptions}
                onChange={(e) => setAccessorialRequestOrOutcome(e.target.value)}
                value={accessorialRequestOrOutcome}
              />
            </RadioButtonGroupWrap>
          )}
          <Box mt={6} />
          <Button
            margin="0 auto"
            onClick={handleConfirm}
            size="large"
            variant="primary"
            isLoading={isLoading}
          >
            Save
          </Button>
          <Box m={3} />
          <Button margin="0 auto" variant="text" onClick={() => setShowDialog(false)}>
            Cancel
          </Button>
        </Wrap>
      </SimpleDialog>
    </>
  )
}

export const IssueSectionV2 = (props: any) => {
  const { record, permissions } = props
  if (!record || !permissions || !record.issues) {
    return null
  }
  const hasAddPermissions = hasPermission(permissions, Permissions.AddIssue)
  const issues = record.issues.map((issue: Issue, index: any) => (
    <IssueItem issue={issue} isLast={index === record.issues.length - 1} />
  ))
  return (
    <FlexBox isColumn alignItems="flex-start">
      {hasAddPermissions && <ReportIssueButton record={record} />}
      <StyledFlexBox isColumn>{issues}</StyledFlexBox>
    </FlexBox>
  )
}
